export const toTimestamp = (date: Date) => ({
  seconds: BigInt(Math.trunc(date.getTime() / 1_000)),
  nanos: (date.getTime() % 1_000) * 1_000_000,
});

export const formatTimestampToDate = (timestamp?: {
  seconds?: string | number | bigint;
  nanos?: number;
}) => {
  if (!timestamp?.seconds) return "-";
  const seconds = Number(timestamp.seconds);
  const nanos = timestamp.nanos ?? 0;
  const date = new Date(seconds * 1000 + Math.trunc(nanos / 1_000_000));
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatTimestampToDateRu = (timestamp?: {
  seconds?: string | number | bigint;
  nanos?: number;
}) => {
  if (!timestamp?.seconds) return "-";
  const seconds = Number(timestamp.seconds);
  const nanos = timestamp.nanos ?? 0;
  const date = new Date(seconds * 1000 + Math.trunc(nanos / 1_000_000));
  // Get locale from navigator or use fallback
  const locale = typeof navigator !== "undefined" ? navigator.language : "ru-RU";
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const toDateParam = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const startOfLocalDay = (date: Date): Date => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
};

// Last representable instant of the local day. Use this for the exclusive-feeling
// upper bound of a range: date pickers hand back midnight, so a range ending at
// startOfLocalDay would drop everything that happened during its final day.
export const endOfLocalDay = (date: Date): Date => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate;
};

export const parseLocalDateParam = (raw?: string): Date | undefined => {
  if (!raw) return undefined;

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!dateOnlyMatch) {
    const parsedDate = new Date(raw);
    return Number.isNaN(parsedDate.getTime()) ? undefined : startOfLocalDay(parsedDate);
  }

  const [, yearRaw, monthRaw, dayRaw] = dateOnlyMatch;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const parsedDate = new Date(year, month - 1, day);

  if (
    Number.isNaN(parsedDate.getTime()) ||
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getDate() !== day
  ) {
    return undefined;
  }

  return startOfLocalDay(parsedDate);
};

export const isSameLocalDay = (left?: Date, right?: Date): boolean =>
  !!left &&
  !!right &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

export const normalizeDateRange = (range: {
  from?: Date;
  to?: Date;
}): {
  from?: Date;
  to?: Date;
} => {
  const from = range.from ? startOfLocalDay(range.from) : undefined;
  const to = range.to ? startOfLocalDay(range.to) : undefined;

  if (!from && !to) return {};
  if (from && !to) return { from, to: from };
  if (!from && to) return { from: to, to };

  if (from!.getTime() <= to!.getTime()) {
    return { from, to };
  }

  return { from: to, to: from };
};

export const toLocalWallClockTimestamp = (date: Date) => {
  const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return toTimestamp(new Date(date.getTime() - timezoneOffsetMs));
};
